import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import classNames from 'classnames';
import { compose } from 'redux';
import { Text } from '@nerdwallet/react-typography';
import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';

import VerticalSpacer from '../vertical-spacer/vertical-spacer';
import TextBox from '../text-box/text-box';
import SmartButton, { NULL_BUTTON_TYPE } from '../smart-button/smart-button';

import validateInterpolation from '../text-box/validate-interpolation';

import {
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  dataDependencyTypePicker,
  dataDependencyNumericIdInput,
  anchorIdInput,
} from '../../lib/editing-configs';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';

import './paragraph.less';

const Paragraph = ({
  preheader,
  header,
  headingStyle,
  alignment,
  mobileAlignment,
  body,
  mobileBody,
  anchorId,
  anchor, // deprecated
  paragraphClass,
  bodyClass,
  smallText,
  size,
  dangerouslyAllowHTMLIn: { header: htmlInHeader, body: htmlInBody },
  fontChronicle,
  data,
  interpolateFrom,
  fullWidthMobile,

  // From SmartButton - use verbatim to allow bringing in propTypes and editingConfig
  buttonType,
  ...smartButtonProps
}) => {
  const offers = Object.values(_.get(data, 'value', {}));
  const interpolationSource = _.get(offers, 0, interpolateFrom);

  const button = (
    <SmartButton
      {...smartButtonProps}
      buttonType={buttonType}
      buttonClass="paragraph__button"
    />
  );

  const paragraphClassName = fullWidthMobile ? 'paragraph--100' : 'paragraph';

  return validateInterpolation({
    interpolationSource,
    templates: { preheader, header, body },
  }) ? (
    <div
      id={anchorId || anchor /* for backwards compatability */}
      className={classNames(`${paragraphClassName}`, paragraphClass, {
        'paragraph-anchor': anchorId || anchor, // for backwards compatability
      })}
    >
      <VerticalSpacer mobile="p-12" tablet="p-12" desktop="p-12" />
      {preheader && (
        <Text color="neutral-dark" uppercase small bold>
          {preheader}
        </Text>
      )}
      {preheader && <VerticalSpacer mobile="p-8" tablet="p-8" desktop="p-8" />}
      {header && (
        <TextBox
          copyStyle={headingStyle}
          textAlign={(size === 'sm' && mobileAlignment) || alignment}
          interpolateFrom={interpolationSource}
          dangerouslyAllowHTML={htmlInHeader}
          text={header}
          fontChronicle={fontChronicle}
        />
      )}
      {header && <VerticalSpacer mobile="p-12" tablet="p-12" desktop="p-12" />}
      <TextBox
        className={bodyClass}
        copyStyle={smallText ? 'copy-p2' : 'copy-p1'}
        textAlign="left"
        text={(size === 'sm' && mobileBody) || body}
        parseLinks
        interpolateFrom={interpolationSource}
        dangerouslyAllowHTML={htmlInBody}
      />
      {buttonType !== NULL_BUTTON_TYPE && (
        <VerticalSpacer mobile="p-24" tablet="p-24" desktop="p-24" />
      )}
      <div className="paragraph__button">
        {button}
        {buttonType !== NULL_BUTTON_TYPE && (
          <VerticalSpacer mobile="p-12" tablet="p-12" desktop="p-12" />
        )}
      </div>
    </div>
  ) : null;
};

Paragraph.propTypes = {
  preheader: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  alignment: PropTypes.string,
  mobileAlignment: PropTypes.string,
  body: PropTypes.node,
  mobileBody: PropTypes.node,
  headingStyle: PropTypes.string,
  paragraphClass: PropTypes.string,
  anchor: PropTypes.string,
  anchorId: PropTypes.string,
  dangerouslyAllowHTMLIn: PropTypes.shape({
    header: PropTypes.bool,
    body: PropTypes.bool,
  }),
  bodyClass: PropTypes.string,
  size: PropTypes.string,
  smallText: PropTypes.bool,
  fontChronicle: PropTypes.bool,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  interpolateFrom: PropTypes.object,
  ...SmartButton.propTypes,
  fullWidthMobile: PropTypes.bool,
};

Paragraph.defaultProps = {
  body: '',
  paragraphClass: '',
  headingStyle: 'copy-h3',
  alignment: 'left',
  mobileAlignment: '',
  dangerouslyAllowHTMLIn: {
    header: false,
    body: false,
  },
  bodyClass: '',
  smallText: false,
  size: '',
  fontChronicle: false,
  data: {},
  interpolateFrom: {},
  ...SmartButton.defaultProps,
  fullWidthMobile: false,
};

Paragraph.displayName = 'Paragraph';

Paragraph.getEditingConfig = (componentDocument) => [
  stringTextAreaSpec('preheader', 'Preheader'),
  stringTextAreaSpec('header', 'Header'),
  stringSelectSpec('alignment', 'Header Alignment', [
    { label: 'Left Align', value: 'left' },
    { label: 'Center Align', value: 'center' },
  ]),
  stringSelectSpec(
    'mobileAlignment',
    'Header Alignment (mobile only)',
    [
      { label: 'Same as Desktop', value: '' },
      { label: 'Left Align', value: 'left' },
      { label: 'Center Align', value: 'center' },
    ],
    {
      help: `If nothing is set, alignment will display the same on mobile and desktop`,
    }
  ),
  boolCheckboxSpec('fontChronicle', 'Header in Chronicle?'),
  stringSelectSpec('headingStyle', 'Heading Style', [
    { label: 'Select one', value: '' },
    { label: 'Heading 1', value: 'copy-h1' },
    { label: 'Heading 2', value: 'copy-h2' },
    { label: 'Heading 3', value: 'copy-h3' },
    { label: 'Heading 4', value: 'copy-h4' },
    { label: 'Heading 5', value: 'copy-h5' },
  ]),
  boolCheckboxSpec('dangerouslyAllowHTMLIn.header', 'Allow HTML in header?', {
    help:
      'WARNING: We strongly advise against making use of this feature unless you really need it.',
  }),
  stringTextAreaSpec('body', 'Body', {
    help: `Want a hyperlink? Write it like this: [Hi!](www.thing.com).
           NW links should be relative, e.g., [Home](/home)`,
  }),
  stringTextAreaSpec('mobileBody', 'Body (mobile only)', {
    help: `If nothing is set, general body text will display on mobile and desktop`,
  }),
  boolCheckboxSpec('smallText', 'Use small text?'),
  boolCheckboxSpec('dangerouslyAllowHTMLIn.body', 'Allow HTML in body?', {
    help:
      'WARNING: We strongly advise against making use of this feature unless you really need it.',
  }),
  boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
    help:
      'The Paragraph defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
  }),
  dataDependencyTypePicker,
  dataDependencyNumericIdInput,
  anchorIdInput,
  ...SmartButton.getEditingConfig(componentDocument),
];

const dataDependencies = (props) => {
  const type = _.get(props, 'dataDependency.type');

  if (!type) {
    return null;
  }

  return {
    data: {
      type,
      ids: _.get(props, 'dataDependency.data.ids', []).map((d) => d.id),
    },
  };
};

const WrappedComponent = compose(
  withMarketplaceContext,
  withOutOfDateMessaging,
  connect(
    createStructuredSelector({
      size: property('mediaQuery'),
    })
  )
)(Paragraph);

export default withDataDependencies(WrappedComponent, dataDependencies);
